// eslint-disable-next-line import/extensions
import { print } from '@apollo/client/utilities';
import { sha256 } from 'js-sha256';
import { addApolloState, getApolloClient } from './client.js';
import { getConfig } from './config/index.js';
import { getPossibleTemplates, getTemplate } from './getTemplate.js';
import { SEED_QUERY } from './queries/seedQuery.js';
import { debugLog, infoLog } from './utils/log.js';
import { hooks } from './wpHooks/index.js';
export const DEFAULT_ISR_REVALIDATE = 60 * 15; // 15 minutes
function isSSR(ctx) {
    return ctx.req !== undefined;
}
const createNotFound = (ctx, revalidate) => (Object.assign({ notFound: true }, (!isSSR(ctx) && { revalidate: revalidate !== null && revalidate !== void 0 ? revalidate : DEFAULT_ISR_REVALIDATE })));
export async function getWordPressProps(options) {
    var _a, _b, _c;
    const { templates } = getConfig();
    if (!templates) {
        throw new Error('Templates are required. Please add them to your config.');
    }
    const { ctx, props, revalidate, extra } = options;
    const client = getApolloClient();
    let resolvedUrl = null;
    if (!isSSR(ctx)) {
        const wordPressNodeParams = (_a = ctx.params) === null || _a === void 0 ? void 0 : _a.wordpressNode;
        if (wordPressNodeParams && Array.isArray(wordPressNodeParams)) {
            resolvedUrl = `/${wordPressNodeParams.join('/')}`;
        }
        else {
            resolvedUrl = '/';
        }
    }
    else {
        resolvedUrl = ctx.resolvedUrl;
        ctx.res.setHeader('x-using', 'faust');
    }
    resolvedUrl = hooks.applyFilters('resolvedUrl', resolvedUrl, {
        nextContext: ctx,
    });
    if (!resolvedUrl) {
        return createNotFound(ctx, revalidate);
    }
    const seedQuery = hooks.applyFilters('seedQueryDocumentNode', SEED_QUERY, {
        resolvedUrl,
    });
    const seedQueryRes = await client.query({
        query: seedQuery,
        variables: { uri: resolvedUrl },
    });
    // Since previews only work for client side, we only need to handle nodeByUri instead of both that and contentNode.
    const seedNode = (_b = seedQueryRes === null || seedQueryRes === void 0 ? void 0 : seedQueryRes.data) === null || _b === void 0 ? void 0 : _b.nodeByUri;
    debugLog(`Seed Node for resolved url: "${resolvedUrl}": `, seedNode);
    if (!seedNode) {
        return createNotFound(ctx, revalidate);
    }
    infoLog(`Possible templates for resolved url: "${resolvedUrl}":`, getPossibleTemplates(seedNode));
    const template = getTemplate(seedNode, templates);
    if (!template) {
        return createNotFound(ctx, revalidate);
    }
    if (template.query && template.queries) {
        throw new Error('`Only either `Component.query` or `Component.queries` can be provided, but not both.');
    }
    let templateQueryRes;
    const templateVariables = (template === null || template === void 0 ? void 0 : template.variables)
        ? template === null || template === void 0 ? void 0 : template.variables(seedNode, {
            asPreview: false,
            locale: ctx.locale,
        }, extra)
        : undefined;
    if (template.query) {
        templateQueryRes = await client.query({
            query: template.query,
            variables: templateVariables,
        });
    }
    let queries = null;
    if (template.queries) {
        const queryCalls = template.queries.map(({ query, variables }) => {
            const queryVariables = variables
                ? variables(seedNode, {
                    asPreview: false,
                    locale: ctx.locale,
                }, extra)
                : undefined;
            return client.query({
                query,
                variables: queryVariables,
            });
        });
        const queriesRes = await Promise.all(queryCalls);
        queries = {};
        queriesRes.forEach((queryRes, index) => {
            if (queries && template.queries) {
                queries[sha256(print(template.queries[index].query))] = queryRes.data;
            }
        });
    }
    const appProps = addApolloState(client, {
        props: Object.assign({ 
            /**
             * The following props may be null coalesced as an "undefined"
             * value is not able to be serialized
             */
            __SEED_NODE__: seedNode !== null && seedNode !== void 0 ? seedNode : null, __TEMPLATE_QUERY_DATA__: (_c = templateQueryRes === null || templateQueryRes === void 0 ? void 0 : templateQueryRes.data) !== null && _c !== void 0 ? _c : null, __TEMPLATE_VARIABLES__: templateVariables !== null && templateVariables !== void 0 ? templateVariables : null, __FAUST_QUERIES__: queries !== null && queries !== void 0 ? queries : null }, props),
    });
    if (!isSSR(ctx)) {
        appProps.revalidate = revalidate !== null && revalidate !== void 0 ? revalidate : DEFAULT_ISR_REVALIDATE;
    }
    return appProps;
}
